import { CalendarIcon, ChevronRight, Loader2, XIcon } from 'lucide-react';
import moment from 'moment';
import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Chip, ChipRow, StickyActionBar } from '@/components/mobile';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { SmartDatetimeInput } from '@/components/ui/extension/smart-datetime-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import CustomerSearch from '@/components/utils/DragAndDropUploader/CustomerSearch';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { MultiUserSelector } from '@/components/runner/utils/MultiUserSelector';
import { cn } from '@/lib/utils';
import type { RecurrenceRule, ReminderCustomer, ReminderPriority } from '@/slices/remindersApi';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { NotificationSettingsField } from '@/components/notifications/NotificationSettingsField';
import {
  newReminderNotifications,
  readNotifications,
  singleOffset,
  type NotificationSource,
  type Notifications,
} from '@/components/notifications/notificationSettings';
import {
  MAX_ASSIGNEES,
  PRIORITY_CONFIG,
  PRIORITY_OPTIONS,
  RECURRENCE_OPTIONS,
  SNOOZE_PRESETS,
  getDefaultNotificationOffset,
  keepNotificationDefault,
} from './reminderHelpers';

interface BusinessUser {
	_id: string;
	name?: string;
	username?: string;
	active?: boolean;
}

export interface ReminderComposerValue {
  text: string;
  dateTime: string;
  notes: string;
  priority: ReminderPriority;
  recurrence: RecurrenceRule;
  notifications: Notifications;
  _assignees: string[];
  customer: ReminderCustomer | null;
}

export interface ReminderComposerSubmit {
  text: string;
  dateTime: string;
  notes: string;
  priority: ReminderPriority;
  recurrence: { rule: RecurrenceRule };
  notifications: Notifications;
  /** Empty means the creator on a new reminder */
  _assignees: string[];
  _customer: string | null;
}

interface ReminderComposerProps {
  initial?: Partial<Omit<ReminderComposerValue, 'notifications'>> & NotificationSource;
  /** Whoever created the reminder being edited; a new one has none yet */
  creatorId?: string;
  users?: BusinessUser[];
  showCustomer?: boolean;
  /** Sequence steps never repeat */
  hideRepeat?: boolean;
  saving?: boolean;
  submitLabel?: string;
  compact?: boolean;
  /**
   * Phone full-screen form: the fields scroll and Save sits in a bar pinned
   * above the keyboard. The parent supplies the Cancel button in its header.
   */
  stickyActions?: boolean;
  onSubmit: (value: ReminderComposerSubmit) => void;
  onCancel?: () => void;
  /**
   * The whole draft whenever it changes (and once when the form opens), so
   * a parent can bring it back as `initial` if the form has to mount again.
   */
  onChange?: (value: ReminderComposerValue) => void;
  /** The Notify panel was open when the form had to mount again */
  notifyOpen?: boolean;
  onNotifyOpenChange?: (open: boolean) => void;
}

// datetime-local wants local wall time without a zone (a machine format).
const toLocalInputValue = (value: string) => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DDTHH:mm') : '';
};

// Every control in the form: one height, border and background
const FIELD_CLASS = 'h-10 bg-background max-md:h-11';

/** A label above its control, the one pattern every section uses */
const Field = ({
  label,
  htmlFor,
  optional = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  optional?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="flex items-baseline gap-1.5">
      {label}
      {optional && <span className="text-xs font-normal text-muted-foreground">Optional</span>}
    </Label>
    {children}
  </div>
);

const getCustomerContactInfo = (customer: ReminderCustomer | null): string => {
  if (!customer) return '';
  if (customer.phone) return formatPhoneForUser(customer.phone);
  if (customer.email) return customer.email;
  if (customer.facebookUserId) return 'Facebook User';
  if (customer.googleConversationId) return 'Google Message';
  return '';
};

const ReminderComposer = ({
  initial,
  creatorId,
  users = [],
  showCustomer = false,
  hideRepeat = false,
  saving = false,
  submitLabel = 'Save reminder',
  compact = false,
  stickyActions = false,
  onSubmit,
  onCancel,
  onChange,
  notifyOpen = false,
  onNotifyOpenChange,
}: ReminderComposerProps) => {
  const maskPhone = usePhoneMask();
  const notifyId = useId();
  // Phones get the native date and time wheels instead of the typed date field.
  const isMobile = useIsMobile();
  const [text, setText] = useState(initial?.text ?? '');
  const [dateTime, setDateTime] = useState(initial?.dateTime ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [priority, setPriority] = useState<ReminderPriority>(initial?.priority ?? 'none');
  const [recurrence, setRecurrence] = useState<RecurrenceRule>(initial?.recurrence ?? 'none');
  const [assignees, setAssignees] = useState<string[]>(initial?._assignees ?? []);
  const [customer, setCustomer] = useState<ReminderCustomer | null>(initial?.customer ?? null);
  // Mode-aware: editing (an initial reminder was passed) shows that reminder's
  // own settings — legacy docs without the field (undefined) mean "at time",
  // NOT the stored default. Creating starts from the device's sticky default.
  // Never `?? 0` here: null means "no notification" and must survive.
  const [initialNotifications] = useState<Notifications>(() =>
    initial
      ? readNotifications(initial, 'reminder')
      : newReminderNotifications(getDefaultNotificationOffset()),
  );
  const [notifications, setNotifications] = useState<Notifications>(initialNotifications);
  // The customer has its own field above, so it doesn't open the details.
  const hasInitialDetails = Boolean(
    (initial?.priority && initial.priority !== 'none') ||
      (initial?.recurrence && initial.recurrence !== 'none') ||
      initial?._assignees?.length ||
      initial?.notes,
  );
  const [showDetails, setShowDetails] = useState(hasInitialDetails);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onChangeRef.current?.({
      text,
      dateTime,
      notes,
      priority,
      recurrence,
      notifications,
      _assignees: assignees,
      customer,
    });
  }, [text, dateTime, notes, priority, recurrence, notifications, assignees, customer]);

	const assignableUsers = users.filter((u) => u.active);

	const canSave = Boolean(text.trim()) && Boolean(dateTime) && !saving;

  const handleSubmit = () => {
    if (!canSave) return;
    // Only an actively-changed timing updates the device's sticky default —
    // submitting untouched (create default or a legacy reminder) is a no-op.
    keepNotificationDefault(notifications, singleOffset(initialNotifications.offsets));
    onSubmit({
      text: text.trim(),
      dateTime,
      notes: notes.trim(),
      priority,
      recurrence: { rule: recurrence },
      notifications,
      _assignees: assignees,
      _customer: customer?._id ?? null,
    });
  };

  return (
    <div className={cn('space-y-4', stickyActions && 'flex min-h-0 flex-1 flex-col space-y-0')}>
      <div
        className={cn(
          'space-y-4',
          stickyActions && 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-4',
        )}
      >
      <Field label="Reminder" htmlFor="reminder-text">
        <Input
          id="reminder-text"
          placeholder="What do you need to remember?"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          disabled={saving}
          // Editing on a phone shouldn't throw the keyboard over the form.
          autoFocus={!(stickyActions && initial)}
          enterKeyHint={stickyActions ? 'done' : undefined}
          autoCapitalize="sentences"
          className={FIELD_CLASS}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) handleSubmit();
          }}
        />
      </Field>

      <Field label="When" htmlFor={isMobile ? 'reminder-when' : undefined}>
        {isMobile ? (
          <>
            <Input
              id="reminder-when"
              type="datetime-local"
              value={toLocalInputValue(dateTime)}
              onChange={(e) =>
                setDateTime(e.target.value ? moment(e.target.value).toISOString() : '')
              }
              disabled={saving}
              className={cn(FIELD_CLASS, '[&::-webkit-date-and-time-value]:text-left')}
            />
            <ChipRow aria-label="Quick times" className={cn('pb-0 pt-1.5', stickyActions ? '-mx-4' : 'px-0')}>
              {SNOOZE_PRESETS.map((preset) => (
                <Chip key={preset.label} onClick={() => setDateTime(preset.getDate())} disabled={saving}>
                  {preset.label}
                </Chip>
              ))}
            </ChipRow>
          </>
        ) : (
          <SmartDatetimeInput
            value={dateTime}
            onValueChange={setDateTime}
            placeholder="e.g. tomorrow at 3pm"
            buttonIcon={<CalendarIcon className="h-4 w-4" />}
            // One 40px box like the other fields: the calendar button and the
            // typed field sit inside it without borders of their own (! beats
            // their 40px touch-screen sizes, which would overflow the box).
            className="h-10 gap-0 border-input bg-background p-0 pl-0.5 shadow-sm focus-within:ring-1 focus-within:ring-ring [&_button]:!size-9 [&_button]:border-0 [&_button]:bg-transparent [&_button]:text-muted-foreground [&_button]:shadow-none [&_input]:!h-9 [&_input]:px-1.5"
            disabled={saving}
          />
        )}
      </Field>

      {showCustomer && (
        <Field label="Customer" optional>
          {customer ? (
            <div className={cn(FIELD_CLASS, 'flex items-center justify-between gap-2 rounded-md border border-input pl-3 pr-1 text-sm shadow-sm')}>
              <div className="flex min-w-0 items-center gap-2">
                <CustomerAvatar
                  customer={customer}
                  aria-hidden
                  className="size-6 shrink-0"
                  fallbackClassName="text-[10px] font-medium"
                />
                <span className="truncate font-medium">{maskPhone(customer.name)}</span>
                <span className="truncate text-muted-foreground">
                  {maskPhone(getCustomerContactInfo(customer))}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCustomer(null)}
                className="size-8 shrink-0 text-muted-foreground hover:text-foreground max-md:size-10"
                aria-label="Clear customer"
                disabled={saving}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <CustomerSearch
              handleSelectCustomer={(c: ReminderCustomer) => setCustomer(c)}
              disabled={saving}
              controlClassName="h-10 min-h-10 shadow-sm max-md:h-11 max-md:min-h-11 max-md:text-base ring-offset-0 [&.ring-2]:ring-1"
              inputClassName="max-md:text-base"
            />
          )}
        </Field>
      )}

      <Field label="Notify" htmlFor={notifyId}>
        <NotificationSettingsField
          id={notifyId}
          kind="reminder"
          variant="field"
          value={notifications}
          onChange={setNotifications}
          assigneeIds={assignees}
          creatorId={creatorId}
          isNew={!creatorId}
          disabled={saving}
          reopen={notifyOpen}
          onOpenChange={onNotifyOpenChange}
        />
      </Field>

      <div className="space-y-4 border-t border-border pt-3">
        <button
          type="button"
          aria-expanded={showDetails}
          className="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md px-1 py-1 text-left text-sm font-medium text-foreground outline-none hover:text-foreground/80 focus-visible:ring-1 focus-visible:ring-ring max-md:min-h-11 max-md:text-[15px]"
          onClick={() => setShowDetails((open) => !open)}
        >
          <ChevronRight
            aria-hidden
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', showDetails && 'rotate-90')}
          />
          <span>{showDetails ? 'Details' : 'Add details'}</span>
          {!showDetails && (
            <span className="ml-auto truncate text-xs font-normal text-muted-foreground max-md:text-[13px]">
              {hideRepeat ? 'Priority, assignee, notes' : 'Priority, repeat, assignee, notes'}
            </span>
          )}
        </button>

        {showDetails && (
          <>
            <div className={cn('grid gap-3', compact || hideRepeat ? 'grid-cols-1' : 'grid-cols-2')}>
              <Field label="Priority">
                <Select
                  value={priority}
                  onValueChange={(v) => setPriority(v as ReminderPriority)}
                  disabled={saving}
                >
                  <SelectTrigger aria-label="Priority" className={cn(FIELD_CLASS, 'max-md:text-base')}>
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        <span className="flex items-center gap-2">
                          <span className={cn('h-2 w-2 rounded-full', PRIORITY_CONFIG[p].dot)} />
                          {p === 'none' ? 'No priority' : `${PRIORITY_CONFIG[p].label} priority`}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {!hideRepeat && (
                <Field label="Repeat">
                  <Select
                    value={recurrence}
                    onValueChange={(v) => setRecurrence(v as RecurrenceRule)}
                    disabled={saving}
                  >
                    <SelectTrigger aria-label="Repeat" className={cn(FIELD_CLASS, 'max-md:text-base')}>
                      <SelectValue placeholder="Repeat" />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            {assignableUsers.length > 0 && (
              <Field label="Assign to">
                <MultiUserSelector
                  title="Unassigned"
                  sheetTitle="Assign to"
                  max={MAX_ASSIGNEES}
                  _selectedUsers={assignees}
                  onSelectUser={(user) =>
                    setAssignees((current) =>
                      current.includes(user._id)
                        ? current.filter((_user) => _user !== user._id)
                        : [...current, user._id],
                    )
                  }
                  disabled={saving}
                  className="min-h-10 w-full border-input bg-background px-3 font-normal shadow-sm max-md:min-h-11 max-md:text-base touch:min-h-11"
                  placeholderClassName="text-sm text-muted-foreground max-md:text-base"
                />
              </Field>
            )}

            <Field label="Notes" htmlFor="reminder-notes">
              <Textarea
                id="reminder-notes"
                placeholder="Anything else to remember"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={5000}
                disabled={saving}
                className="min-h-[72px] bg-background"
              />
            </Field>
          </>
        )}
      </div>
      </div>

      {stickyActions ? (
        <StickyActionBar className="pb-[max(0.5rem,var(--safe-bottom))] kb-open:pb-2">
          <Button className="flex-1" onClick={handleSubmit} disabled={!canSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
          </Button>
        </StickyActionBar>
      ) : (
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button size="sm" onClick={handleSubmit} disabled={!canSave}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
        </Button>
      </div>
      )}
    </div>
  );
};

export default ReminderComposer;
